import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import DOMPurify from "dompurify";
import parse from "html-react-parser";
import fetchService from "Services/getApidataStrapi";
import {
  STRAPI_API_DISCLOSURE_STATEMENT_RECUR_EN,
  STRAPI_API_DISCLOSURE_STATEMENT_UK,
} from "Routes/Main/constants";
import {Grid, Paper } from "@material-ui/core";
import CommonLoader from 'Views/Common/CommonLoader';
import LayoutView from "Views/Common/LayoutView";
import { SectionHeader } from 'components';
import clsx from 'clsx';
import { useCountryConfig } from 'hooks/useCountryConfig';
import { useInitializeCountryConfig } from "hooks/useInitializeCountryConfig";
import { TERMS_DETAILS_REPLACE_MACROS } from 'Views/Common/constants';
import { GROUP_SERVICES } from 'Services';

const Terms = (props) => {
  // Use the useCountryConfig hook to get country config
  useInitializeCountryConfig();
  const { countryCode } = useCountryConfig();
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("Terms & Conditions");
  const [disclosureData, setDisclosureData] = useState();
  const [detailsLoading, setDetailsLoading] = useState(false);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const groupId = queryParams.get("group_id");

  const phone = queryParams.get("phone");

  
  // Get countryCode from hook or query string as fallback
  const currentCountryCode = countryCode;
  
  const isReadyToLoad = Boolean(currentCountryCode);

  // Helper to fetch Strapi data (supports old attributes and new document response formats)
  const getStrapiData = async (apiPath) => {
    try {
      const response = await fetchService(apiPath);
      return response;
    }
    catch (err) {
      console.error('Strapi API Error:', err);
      throw err;
    }
  };

  const updateDisclosureContent = (disclosureDetails) => {
    const {
      group_name = '',
      group_url = '',
      email: group_email,
      max_additional_members: group_members = 0,
      patient_website = '',
      industry = '',
      patient_support_email = '',
    } = disclosureDetails;


    if (disclosureData) {
      // Create the proper structure for the modal with replaced content
      const processedContent = disclosureData
        .replaceAll(TERMS_DETAILS_REPLACE_MACROS.group_name, group_name)
        .replaceAll(TERMS_DETAILS_REPLACE_MACROS.group_url, group_url)
        .replaceAll(TERMS_DETAILS_REPLACE_MACROS.group_email, group_email)
        .replaceAll(TERMS_DETAILS_REPLACE_MACROS.group_members, group_members)
        .replaceAll(TERMS_DETAILS_REPLACE_MACROS.group_patient_portal, patient_website)
        .replaceAll(TERMS_DETAILS_REPLACE_MACROS.group_industry, industry.toLowerCase())
        .replaceAll(TERMS_DETAILS_REPLACE_MACROS.subscribili_support_email, patient_support_email)
        .replaceAll(TERMS_DETAILS_REPLACE_MACROS.group_phone, phone || '')
        .replaceAll(TERMS_DETAILS_REPLACE_MACROS.group_support_phone, phone || '')
      setContent(processedContent);
    }
  };

  const getDisclosureDetails = async () => {
    if (!groupId || groupId === 'ALL_GROUPS') {
      setDetailsLoading(false);
      return;
    }

    setDetailsLoading(true);
    try {
      const response = await GROUP_SERVICES.getDisclosureDetails(groupId);
      if (response?.type === 'success' && response?.data) {
        updateDisclosureContent(response.data);
      } else {
        throw response;
      }
    } catch (err) {
      console.log('[Terms] Error fetching group details:', err);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Fetch content based on country
  const fetchTermsContent = async () => {
    setLoading(true);
    try {
      const apiPath =
        currentCountryCode === "GB"
          ? STRAPI_API_DISCLOSURE_STATEMENT_UK
          : STRAPI_API_DISCLOSURE_STATEMENT_RECUR_EN;


      const strapiResponse = await getStrapiData(apiPath);
      

      setTitle("Terms & Conditions");
      setDisclosureData(strapiResponse?.data?.description);
      
    } catch (err) {
      console.error("[Terms] Error loading terms:", err);
      setContent("Unable to load Terms & Conditions. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch Strapi content when country is available
  useEffect(() => {
    if (isReadyToLoad) {
      fetchTermsContent();
    }
  }, [currentCountryCode, isReadyToLoad]);

  // Fetch group details when both disclosureData and groupId are available
  useEffect(() => {
    if (disclosureData && groupId) {
      getDisclosureDetails();
    }
  }, [disclosureData, groupId]);

  // Strapi can return either Markdown or HTML; render HTML with html-react-parser so tags aren't shown as text
  const isHtmlContent = (text) => typeof text === "string" && /^\s*</.test(text.trim());
  const renderContent = (text) => {
    if (isHtmlContent(text)) {
      const sanitizedHtml = DOMPurify.sanitize(text);
  
      return (
        <div
          className="content"
          style={{ fontSize: "16px", color: "#333", lineHeight: "1.6" }}
        >
          {parse(sanitizedHtml)}
        </div>
      );
    }
  
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
      >
        {text}
      </ReactMarkdown>
    );
  };
  
  return (
    
    <>
      <SectionHeader
        title=""
        rightSection={
            currentCountryCode === "GB" ? (
              // UK VERSION
            <Paper className={clsx("widget-wrapper")} elevation={0}>
                {loading || !isReadyToLoad || (groupId && detailsLoading) ? (
                  <CommonLoader />
                ) : (
                  <Grid item xs={12}>
                {content ? (
                  <div className="uk-terms-content" style={{ fontSize: "16px", color: "#333", lineHeight: "1.6" }}>
                    <style>
                      {`
                        .uk-terms-content p {
                          margin: 0 0 16px 0;
                          line-height: 1.6;
                          font-size: 16px;
                          color: #333;
                        }
                        .uk-terms-content h1,
                        .uk-terms-content h2,
                        .uk-terms-content h3,
                        .uk-terms-content h4,
                        .uk-terms-content h5,
                        .uk-terms-content h6 {
                          margin: 24px 0 12px 0;
                          font-weight: 600;
                          color: #111;
                          line-height: 1.3;
                        }
                        .uk-terms-content h1 { font-size: 28px; }
                        .uk-terms-content h2 { font-size: 24px; }
                        .uk-terms-content h3 { font-size: 20px; }
                        .uk-terms-content h4 { font-size: 18px; }
                        .uk-terms-content h5 { font-size: 16px; }
                        .uk-terms-content h6 { font-size: 14px; }
                        .uk-terms-content ul,
                        .uk-terms-content ol {
                          margin: 0 0 16px 0;
                          padding-left: 24px;
                        }
                        .uk-terms-content li {
                          margin: 0 0 8px 0;
                          line-height: 1.6;
                          font-size: 16px;
                          color: #333;
                        }
                        .uk-terms-content strong {
                          font-weight: 600;
                          color: #111;
                        }
                        .uk-terms-content a {
                          color: #1976d2;
                          text-decoration: underline;
                        }
                        .uk-terms-content blockquote {
                          border-left: 4px solid #ddd;
                          margin: 0 0 16px 0;
                          padding: 8px 16px;
                          color: #555;
                        }
                      `}
                    </style>
                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                      {content}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <div>No Terms and Conditions found for this region.</div>
                )}
              </Grid>
                )}
              </Paper>
            ) : (
              // 🌎 DEFAULT VERSION (non-GB)
              <Paper className={clsx("widget-wrapper mg_top_32")} elevation={0}>
                {loading || !isReadyToLoad || (groupId && detailsLoading) ? (
                  <CommonLoader />
                ) : (
                  <Grid item xs={12}>
                    <div style={{ fontSize: "14px" }}>
                      {content ? (
                        renderContent(content)
                      ) : (
                        <div>No Terms & Conditions found for this region.</div>
                      )}
                    </div>
                  </Grid>
                )}
              </Paper>
            )
          }
          
      >
      </SectionHeader>
    </>
  );
};

export default Terms;
